/* @jsx h */
import { defineComponent, ref, h, computed } from "vue";
import { useMenu } from "@app/vue/providers/index";
import { ContactForm } from "./ContactForm";
import { useLocalization } from "@lemmonhq/uikit";

export const EventReservationPanel = defineComponent({
  name: "EventReservationPanel",
  setup() {
    const { shop, selectedEvent } = useMenu();
    const { locale, t } = useLocalization();
    const quantity = ref(2);
    const selectedTicketId = ref<string>("");
    const selectedTimeSlot = ref<string>("");
    const name = ref("");
    const email = ref("");
    const phone = ref("");
    const isSubmitting = ref(false);

    const event = computed(() => selectedEvent.value?.event);
    const preselectedTicket = computed(() => selectedEvent.value?.ticket);
    const preselectedQuantity = computed(
      () => selectedEvent.value?.quantity || 2,
    );
    const preselectedTimeSlot = computed(() => selectedEvent.value?.timeSlot);

    // Initialize with preselected values if available
    if (preselectedTicket.value) {
      selectedTicketId.value = preselectedTicket.value.id;
    }
    if (preselectedQuantity.value) {
      quantity.value = preselectedQuantity.value;
    }
    if (preselectedTimeSlot.value) {
      selectedTimeSlot.value = preselectedTimeSlot.value;
    }

    const selectedTicket = computed(() => {
      if (!event.value) return null;
      return (
        event.value.ticket_types?.find(
          (t) => t.id === selectedTicketId.value,
        ) || null
      );
    });

    const totalPrice = computed(() => {
      if (!selectedTicket.value) return 0;
      return selectedTicket.value.price * quantity.value;
    });

    const isFormValid = computed(() => {
      const hasRequiredFields =
        selectedTicket.value &&
        quantity.value > 0 &&
        name.value.trim() &&
        email.value.trim() &&
        phone.value.trim();

      // Note: hasTimeSlots property doesn't exist in Event type
      // Time slots would be determined by ticket_types having time property
      // For now, keeping simple validation

      return hasRequiredFields;
    });

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString(locale.value || "en", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    };

    const handleQuantityChange = (delta: number) => {
      const newQuantity = quantity.value + delta;
      // Note: available property doesn't exist in EventTicket type
      const maxAvailable = 999;
      if (newQuantity >= 1 && newQuantity <= maxAvailable) {
        quantity.value = newQuantity;
      }
    };

    const handleSubmit = async () => {
      if (!isFormValid.value) return;

      isSubmitting.value = true;
      try {
        // TODO: Implement API call
        const reservationData = {
          event_id: event.value?.id,
          event_title: event.value?.title,
          ticket_type: selectedTicket.value?.name,
          quantity: quantity.value,
          total_price: totalPrice.value,
          time_slot: selectedTimeSlot.value || event.value?.time,
          date: event.value?.date,
          name: name.value,
          email: email.value,
          phone: phone.value,
        };
        console.log("Submitting event reservation:", reservationData);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        alert(t("customer.reserve_events.reservation_submitted"));
      } catch (error) {
        console.error("Error submitting event reservation:", error);
        alert(t("customer.reserve.submit_failed"));
      } finally {
        isSubmitting.value = false;
      }
    };

    return () => {
      if (!event.value) {
        return (
          <div class="bg-white rounded-lg border border-gray-200 p-6 text-center">
            <span class="material-symbols-outlined text-4xl text-gray-300 mb-2 block">
              event_busy
            </span>
            <p class="text-gray-600 font-light">
              {t("customer.reserve_events.no_event_selected")}
            </p>
          </div>
        );
      }

      return (
        <div class="space-y-6">
          {/* Event Summary */}
          <div class="bg-white rounded-lg border border-gray-200 p-4">
            <div class="flex items-start gap-3 mb-3">
              <span class="material-symbols-outlined text-gray-500">
                confirmation_number
              </span>
              <div class="flex-1">
                <h3 class="font-medium text-gray-900 mb-1">
                  {event.value.title}
                </h3>
                {event.value.subtitle && (
                  <p class="text-sm text-gray-600 font-light">
                    {event.value.subtitle}
                  </p>
                )}
              </div>
            </div>

            <div class="space-y-2 text-sm">
              <div class="flex items-center gap-2 text-gray-600">
                <span class="material-symbols-outlined text-lg">
                  calendar_today
                </span>
                <span class="font-light">{formatDate(event.value.date)}</span>
              </div>
              <div class="flex items-center gap-2 text-gray-600">
                <span class="material-symbols-outlined text-lg">schedule</span>
                <span class="font-light">
                  {selectedTimeSlot.value || event.value.time}
                </span>
              </div>
              <div class="flex items-center gap-2 text-gray-600">
                <span class="material-symbols-outlined text-lg">
                  location_on
                </span>
                <span class="font-light">{event.value.location}</span>
              </div>
            </div>
          </div>

          {/* Time Slot Selection */}
          {/* Note: hasTimeSlots and timeSlots don't exist in Event type - removing this section */}

          {/* Ticket Selection */}
          <div class="bg-white rounded-lg border border-gray-200 p-4">
            <h3 class="font-medium text-gray-900 mb-3">
              {t("customer.reserve_events.select_ticket_type")}
            </h3>
            <div class="space-y-2">
              {event.value.ticket_types?.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => (selectedTicketId.value = ticket.id)}
                  class={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedTicketId.value === ticket.id
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900 text-sm">
                        {ticket.name}
                      </div>
                      {ticket.description && (
                        <div class="text-xs text-gray-500 font-light mt-0.5">
                          {ticket.description}
                        </div>
                      )}
                    </div>
                    <div class="text-right ml-3">
                      <div class="font-light text-gray-900">
                        {shop.value.currency}
                        {ticket.price}
                      </div>
                      <div class="text-xs text-gray-500">
                        {t("customer.reserve_events.plus_tax")}
                      </div>
                    </div>
                  </div>

                  {/* Includes */}
                  {ticket.includes && ticket.includes.length > 0 && (
                    <ul class="mt-2 space-y-1">
                      {ticket.includes.map((item, idx) => (
                        <li
                          key={idx}
                          class="flex items-start gap-1.5 text-xs text-gray-600"
                        >
                          <span class="material-symbols-outlined text-sm text-gray-400 mt-0.5">
                            check
                          </span>
                          <span class="font-light">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Note: available property doesn't exist in EventTicket type - commenting out */}
                </div>
              ))}
            </div>
          </div>

          {/* Number of Guests */}
          {selectedTicket.value && (
            <div class="bg-white rounded-lg border border-gray-200 p-4">
              <label class="block text-sm font-medium text-gray-700 mb-3">
                {t("customer.reserve_events.number_of_tickets")}
              </label>
              <div class="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity.value <= 1}
                  class="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <span class="material-symbols-outlined text-gray-600">
                    remove
                  </span>
                </button>
                <span class="text-2xl font-light w-12 text-center">
                  {quantity.value}
                </span>
                <button
                  type="button"
                  onClick={() => handleQuantityChange(1)}
                  disabled={false}
                  class="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <span class="material-symbols-outlined text-gray-600">
                    add
                  </span>
                </button>
              </div>

              {/* Price Summary */}
              <div class="mt-4 pt-4 border-t border-gray-100">
                <div class="flex justify-between items-center text-sm mb-2">
                  <span class="text-gray-600 font-light">
                    {quantity.value}x {selectedTicket.value.name}
                  </span>
                  <span class="font-light text-gray-900">
                    {shop.value.currency}
                    {selectedTicket.value.price * quantity.value}
                  </span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="font-medium text-gray-900">
                    {t("customer.reserve_events.total")}
                  </span>
                  <div class="text-right">
                    <div class="font-light text-xl text-gray-900">
                      {shop.value.currency}
                      {totalPrice.value}
                    </div>
                    <div class="text-xs text-gray-500">
                      {t("customer.reserve_events.plus_tax")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contact Information */}
          <ContactForm
            name={name.value}
            email={email.value}
            phone={phone.value}
            onUpdate:name={(v: string) => (name.value = v)}
            onUpdate:email={(v: string) => (email.value = v)}
            onUpdate:phone={(v: string) => (phone.value = v)}
          />

          {/* Event Info */}
          {event.value.need_to_know && event.value.need_to_know.length > 0 && (
            <div class="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 class="text-sm font-medium text-gray-900 mb-3">
                {t("customer.reserve_events.need_to_know")}
              </h4>
              <ul class="space-y-2">
                {event.value.need_to_know.map((info, idx) => (
                  <li
                    key={idx}
                    class="flex items-start gap-2 text-xs text-gray-600"
                  >
                    <span class="material-symbols-outlined text-sm text-gray-400 mt-0.5">
                      info
                    </span>
                    <span class="font-light">{info}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Reserve Button - Sticky at top */}
          <div class="sticky bottom-4 z-10">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid.value || isSubmitting.value}
              class="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-light hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isSubmitting.value
                ? t("customer.reserve.reserving")
                : t("customer.reserve_events.reserve_tickets")}
            </button>
          </div>
        </div>
      );
    };
  },
});
